<script>
    import { createEventDispatcher } from 'svelte';
    import { slide } from 'svelte/transition';
    import { commentThreadsStore } from '../../stores/commentsStore.js';
    import { commentAnchorService } from '../../services/commentAnchorService.js';
    // import { collaborationService } from '../../services/collaborationService.js';
    
    export let thread;
    
    // Custom action to focus element on mount
    function focusOnMount(node) {
        // Use setTimeout to ensure the element is fully rendered
        setTimeout(() => {
            node.focus();
        }, 50);
        
        return {
            destroy() {
                // Nothing to clean up
            }
        };
    }
    
    const dispatch = createEventDispatcher();
    
    let isExpanded = true;
    let replyText = '';
    let showReplyForm = false;
    let newCommentText = '';
    let isEditingNew = thread.isNew || false;
    
    function toggleExpanded() {
        isExpanded = !isExpanded;
    }
    
    function toggleResolved() {
        const updatedThread = {
            ...thread,
            resolved: !thread.resolved,
            resolvedAt: !thread.resolved ? new Date().toISOString() : null,
            resolvedBy: !thread.resolved ? window.currentUser?.id : null
        };
        
        // Update local store
        commentThreadsStore.updateThread(thread.id, updatedThread);
        
        // Update Yjs when collaboration service is available
        // TODO: Re-enable when collaboration is initialized
        // const doc = collaborationService.getDocument('current');
        // const commentsArray = doc.getArray('comments');
        // const index = commentsArray.toArray().findIndex(c => c.id === thread.id);
        // if (index !== -1) {
        //     commentsArray.delete(index, 1);
        //     commentsArray.insert(index, [updatedThread]);
        // }
    }
    
    function showReply() {
        showReplyForm = true;
    }
    
    function cancelReply() {
        showReplyForm = false;
        replyText = '';
    }
    
    function submitReply() {
        if (!replyText.trim()) return;
        
        const reply = {
            id: `reply-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: replyText.trim(),
            userId: window.currentUser?.id,
            userName: window.currentUser?.name || 'Anonymous',
            timestamp: new Date().toISOString()
        };
        
        const updatedThread = {
            ...thread,
            replies: [...(thread.replies || []), reply]
        };
        
        // Update local store
        commentThreadsStore.updateThread(thread.id, updatedThread);
        
        // Update Yjs when collaboration service is available
        // TODO: Re-enable when collaboration is initialized
        // const doc = collaborationService.getDocument('current');
        // const commentsArray = doc.getArray('comments');
        // const index = commentsArray.toArray().findIndex(c => c.id === thread.id);
        // if (index !== -1) {
        //     commentsArray.delete(index, 1);
        //     commentsArray.insert(index, [updatedThread]);
        // }
        
        // Reset form
        cancelReply();
    }
    
    function navigateToPosition() {
        // If thread has an anchor, use the anchor service for navigation
        if (thread.anchorId) {
            const editor = thread.documentType === 'spec' ? window.specEditor : window.claimsEditor;
            if (editor) {
                const navigated = commentAnchorService.navigateToAnchor(thread.anchorId, editor);
                if (navigated) {
                    // Also highlight the anchor temporarily
                    const anchorElement = document.querySelector(`[data-anchor-id="${thread.anchorId}"]`);
                    if (anchorElement) {
                        anchorElement.classList.add('comment-anchor-active');
                        setTimeout(() => {
                            anchorElement.classList.remove('comment-anchor-active');
                        }, 2000);
                    }
                    return;
                }
            }
        }
        
        // Fallback to old navigation method
        dispatch('navigate', { 
            documentType: thread.documentType,
            position: thread.position 
        });
    }
    
    function formatTimestamp(timestamp) {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString();
    }
    
    function saveNewComment() {
        if (!newCommentText.trim()) return;
        
        const updatedThread = {
            ...thread,
            text: newCommentText.trim(),
            isNew: false
        };
        
        // Update the thread
        commentThreadsStore.updateThread(thread.id, updatedThread);
        
        // Clear edit state
        isEditingNew = false;
        newCommentText = '';
    }
    
    function cancelNewComment() {
        // Remove the thread if user cancels
        commentThreadsStore.removeThread(thread.id);
        
        // Also remove the anchor
        if (thread.anchorId) {
            commentAnchorService.removeAnchor(thread.anchorId);
        }
    }
    
    function deleteThread() {
        // Confirm deletion
        if (!confirm('Are you sure you want to delete this comment and all its replies?')) {
            return;
        }
        
        // Remove the thread
        commentThreadsStore.removeThread(thread.id);
        
        // Remove the anchor
        if (thread.anchorId) {
            commentAnchorService.removeAnchor(thread.anchorId);
        }
        
        // TODO: Remove from Yjs when collaboration is re-enabled
    }
    
    function deleteReply(replyId) {
        // Confirm deletion
        if (!confirm('Are you sure you want to delete this reply?')) {
            return;
        }
        
        // Remove the reply from the thread
        const updatedThread = {
            ...thread,
            replies: thread.replies.filter(r => r.id !== replyId)
        };
        
        // Update the thread
        commentThreadsStore.updateThread(thread.id, updatedThread);
        
        // TODO: Update Yjs when collaboration is re-enabled
    }
</script>

<div class="comment-thread" class:resolved={thread.resolved}>
    <div class="thread-header">
        <button
            class="thread-toggle"
            on:click={toggleExpanded}
            aria-expanded={isExpanded}
        >
            <i class="fas fa-chevron-{isExpanded ? 'down' : 'right'}"></i>
        </button>
        
        <div class="thread-info">
            <div class="thread-meta">
                <span class="thread-author">{thread.userName}</span>
                <span class="thread-time">{formatTimestamp(thread.timestamp)}</span>
                {#if thread.resolved}
                    <span class="resolved-badge">
                        <i class="fas fa-check-circle"></i> Resolved
                    </span>
                {/if}
            </div>
            {#if thread.anchorId && thread.snippet}
                <button
                    class="thread-location"
                    on:click={navigateToPosition}
                    title="Go to comment location"
                    class:has-anchor={true}
                >
                    <i class="fas fa-link"></i>
                    {thread.documentType === 'spec' ? 'Spec' : 'Claims'} • 
                    {thread.snippet}
                </button>
            {:else}
                <span class="thread-location no-anchor">
                    <i class="fas fa-exclamation-triangle"></i>
                    No text anchor (legacy comment)
                </span>
            {/if}
        </div>
        
        <div class="thread-actions">
            {#if !thread.resolved}
                <button
                    class="action-button"
                    on:click={toggleResolved}
                    title="Mark as resolved"
                >
                    <i class="fas fa-check"></i>
                </button>
            {:else}
                <button
                    class="action-button"
                    on:click={toggleResolved}
                    title="Reopen"
                >
                    <i class="fas fa-undo"></i>
                </button>
            {/if}
            {#if thread.userId === window.currentUser?.id}
                <button
                    class="action-button delete-button"
                    on:click={deleteThread}
                    title="Delete comment"
                >
                    <i class="fas fa-trash"></i>
                </button>
            {/if}
        </div>
    </div>
    
    {#if isExpanded}
        <div class="thread-content" transition:slide={{ duration: 200 }}>
            {#if isEditingNew}
                <div class="new-comment-form">
                    <textarea
                        bind:value={newCommentText}
                        placeholder="Write your comment..."
                        class="comment-input"
                        on:keydown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                saveNewComment();
                            }
                            if (e.key === 'Escape') {
                                cancelNewComment();
                            }
                        }}
                        use:focusOnMount
                    ></textarea>
                    <div class="form-actions">
                        <button class="button-secondary" on:click={cancelNewComment}>
                            Cancel
                        </button>
                        <button 
                            class="button-primary" 
                            on:click={saveNewComment}
                            disabled={!newCommentText.trim()}
                        >
                            Post Comment
                        </button>
                    </div>
                </div>
            {:else}
                <div class="comment-text">
                    {thread.text}
                </div>
            {/if}
            
            {#if thread.replies && thread.replies.length > 0}
                <div class="replies">
                    {#each thread.replies as reply (reply.id)}
                        <div class="reply">
                            <div class="reply-header">
                                <div class="reply-meta">
                                    <span class="reply-author">{reply.userName}</span>
                                    <span class="reply-time">{formatTimestamp(reply.timestamp)}</span>
                                </div>
                                {#if reply.userId === window.currentUser?.id || thread.userId === window.currentUser?.id}
                                    <button
                                        class="reply-delete-button"
                                        on:click={() => deleteReply(reply.id)}
                                        title="Delete reply"
                                    >
                                        <i class="fas fa-times"></i>
                                    </button>
                                {/if}
                            </div>
                            <div class="reply-text">{reply.text}</div>
                        </div>
                    {/each}
                </div>
            {/if}
            
            {#if !thread.resolved}
                {#if showReplyForm}
                    <div class="reply-form" transition:slide={{ duration: 200 }}>
                        <textarea
                            bind:value={replyText}
                            placeholder="Write a reply..."
                            class="reply-input"
                            on:keydown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    submitReply();
                                }
                            }}
                        ></textarea>
                        <div class="reply-actions">
                            <button class="button-secondary" on:click={cancelReply}>
                                Cancel
                            </button>
                            <button 
                                class="button-primary" 
                                on:click={submitReply}
                                disabled={!replyText.trim()}
                            >
                                Reply
                            </button>
                        </div>
                    </div>
                {:else}
                    <button class="reply-button" on:click={showReply}>
                        <i class="fas fa-reply"></i> Reply
                    </button>
                {/if}
            {/if}
        </div>
    {/if}
</div>

<style>
    .comment-thread {
        position: relative;
        margin-bottom: 16px;
        background: var(--color-surface-panel-comments);
        border: 2px solid var(--color-comments-border, #FDE68A);
        border-radius: 8px;
        box-shadow: var(--shadow-sm);
        transition: all 0.2s ease;
    }
    
    .comment-thread:hover {
        border-color: var(--color-comments-hover, #FBBF24);
        box-shadow: var(--shadow-md);
    }
    
    .comment-thread.resolved {
        border-color: var(--color-success);
        opacity: 0.7;
    }
    
    .thread-header {
        display: flex;
        align-items: flex-start;
        padding: 12px;
        gap: 8px;
        background: var(--color-surface-header-comments);
        border-radius: 6px 6px 0 0;
        border-bottom: 1px solid var(--color-comments-border, #FDE68A);
    }
    
    .thread-toggle {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px;
        color: var(--color-text-secondary);
        background: none;
        border: none;
        border-radius: 4px;
        transition: background-color 0.2s;
        cursor: pointer;
    }
    
    .thread-toggle:hover {
        background: var(--color-background-hover);
    }
    
    .thread-info {
        flex: 1;
    }
    
    .thread-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
    }
    
    .thread-author {
        font-weight: 600;
        color: var(--color-text);
    }
    
    .thread-time {
        font-size: 12px;
        color: var(--color-text-secondary);
    }
    
    .resolved-badge {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: var(--color-success);
        gap: 4px;
    }
    
    .thread-location {
        padding: 0;
        font-size: 12px;
        text-align: left;
        color: var(--color-link);
        background: none;
        border: none;
        transition: color 0.2s;
        cursor: pointer;
    }
    
    .thread-location:hover {
        text-decoration: underline;
        color: var(--color-link-hover);
    }
    
    .thread-location.has-anchor {
        font-weight: 500;
        color: var(--color-link);
    }
    
    .thread-location.has-anchor i {
        color: var(--color-comments-text, #92400E);
    }
    
    .thread-location.no-anchor {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: var(--color-error);
        font-style: italic;
        cursor: default;
    }
    
    .thread-location.no-anchor i {
        color: var(--color-error);
    }
    
    .thread-actions {
        display: flex;
        gap: 4px;
    }
    
    .action-button {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px 8px;
        color: var(--color-text-secondary);
        background: none;
        border: none;
        border-radius: 4px;
        transition: all 0.2s;
        cursor: pointer;
    }
    
    .action-button:hover {
        color: var(--color-text);
        background: var(--color-background-hover);
    }
    
    .delete-button:hover {
        color: var(--color-error);
        background: var(--color-error-bg, var(--color-surface-error));
    }
    
    .thread-content {
        padding: 16px 12px 12px 36px;
    }
    
    .comment-text {
        margin-bottom: 12px;
        line-height: 1.5;
        color: var(--color-text);
    }
    
    .replies {
        padding-top: 12px;
        padding-bottom: 4px;
        padding-left: 16px;
        margin-top: 16px;
        margin-left: 20px;
        border-radius: 4px;
        border-left: 3px solid var(--color-comments-border, #FDE68A);
    }
    
    .reply {
        margin-bottom: 12px;
    }
    
    .reply-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;
    }
    
    .reply-meta {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    
    .reply-author {
        font-size: 13px;
        font-weight: 600;
    }
    
    .reply-time {
        font-size: 11px;
        color: var(--color-text-secondary);
    }
    
    .reply-text {
        font-size: 14px;
        line-height: 1.4;
    }
    
    .reply-delete-button {
        padding: 2px 6px;
        font-size: 12px;
        color: var(--color-text-tertiary);
        background: none;
        border: none;
        border-radius: 4px;
        opacity: 0.6;
        transition: all 0.2s;
        cursor: pointer;
    }
    
    .reply-delete-button:hover {
        color: var(--color-error);
        background: var(--color-error-bg, var(--color-surface-error));
        opacity: 1;
    }
    
    .reply-button {
        display: flex;
        align-items: center;
        padding: 4px 12px;
        font-size: 13px;
        color: var(--color-text-secondary);
        background: none;
        border: 1px solid var(--color-border);
        border-radius: 4px;
        transition: all 0.2s;
        cursor: pointer;
        gap: 4px;
    }
    
    .reply-button:hover {
        color: var(--color-text);
        background: var(--color-background-hover);
    }
    
    .reply-form {
        margin-top: 12px;
    }
    
    .reply-input {
        width: 100%;
        min-height: 60px;
        padding: 8px;
        font-size: 13px;
        color: var(--color-text);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: 4px;
        resize: vertical;
    }
    
    .reply-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 8px;
    }
    
    .button-primary,
    .button-secondary {
        padding: 6px 12px;
        font-size: 13px;
        border: 1px solid var(--color-border);
        border-radius: 4px;
        transition: all 0.2s;
        cursor: pointer;
    }
    
    .button-primary {
        color: var(--color-comments-text, #92400E);
        background: var(--color-comments-badge, #FDE68A);
        border-color: var(--color-comments-border, #FDE68A);
    }
    
    .button-primary:hover:not(:disabled) {
        background: var(--color-comments-header, #FEF3C7);
    }
    
    .button-primary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    
    .button-secondary {
        color: var(--color-text);
        background: var(--color-surface);
    }
    
    .button-secondary:hover {
        background: var(--color-background-hover);
    }
    
    /* Dark mode adjustments */
    :global([data-theme="dark"]) .thread-header {
        background: var(--color-surface-header-comments-frosted);
        border-bottom-color: var(--color-comments-border-dark);
    }
    
    :global([data-theme="dark"]) .comment-thread {
        border-color: var(--color-comments-border-dark);
    }
    
    :global([data-theme="dark"]) .comment-thread:hover {
        border-color: var(--color-comments-hover-dark);
    }
    
    :global([data-theme="dark"]) .replies {
        border-left-color: var(--color-comments-border-dark);
    }
    
    :global([data-theme="dark"]) .button-secondary {
        background: var(--color-surface-panel-comments);
    }
    
    /* New comment form styles */
    .new-comment-form {
        width: 100%;
    }
    
    .comment-input {
        width: 100%;
        min-height: 80px;
        padding: 8px 12px;
        font-family: inherit;
        font-size: 14px;
        line-height: 1.5;
        color: var(--color-text);
        background: var(--color-surface-content-comments);
        border: 2px solid var(--color-comments-border);
        border-radius: 4px;
        resize: vertical;
    }
    
    .comment-input:focus {
        border-color: var(--color-comments-hover);
        outline: none;
        box-shadow: var(--shadow-focus-comments, var(--shadow-focus));
    }
    
    .comment-input::placeholder {
        color: var(--color-text-tertiary);
    }
    
    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 8px;
    }
    
    /* Dark mode for new comment form */
    :global([data-theme="dark"]) .comment-input {
        color: var(--color-text);
        background: var(--color-surface-panel-comments);
        border-color: var(--color-comments-border-dark);
    }
    
    :global([data-theme="dark"]) .comment-input:focus {
        border-color: var(--color-comments-hover-dark);
    }
    
    :global([data-theme="dark"]) .new-comment-form .button-primary {
        color: var(--color-comments-text-dark);
        background: var(--color-comments-badge-dark);
        border-color: var(--color-comments-border-dark);
    }
    
    :global([data-theme="dark"]) .new-comment-form .button-primary:hover:not(:disabled) {
        background: var(--color-comments-hover-dark);
    }
    
    :global([data-theme="dark"]) .new-comment-form .button-secondary {
        color: var(--color-text);
        background: var(--color-surface-panel-comments);
        border-color: var(--color-comments-border-dark);
    }
    
    :global([data-theme="dark"]) .new-comment-form .button-secondary:hover {
        background: var(--color-surface-content-comments);
    }
</style>